import { Button, Tabs } from '@prio365/prio365-react-library';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Flex from '../../../../components/Flex';
import PrioSpinner from '../../../../components/PrioSpinner';
import {
  ContactImportAction,
  ContactImportProcessDto,
  ContactImportState,
} from '../../../../models/ContactImportProcess';
import { makePrioStyles } from '../../../../theme/utils';
import {
  apiFetchContactImportProcess,
  apiStartContactImportProcess,
  apiValidateContactImportProcess,
  pollProcessState,
} from '../../api';
import { ExternalCompanyTab } from './tabs/ExternalCompanyTab';
import { ExternalContactTab } from './tabs/ExternalContactTab';

const useStyles = makePrioStyles((theme) => ({
  root: { height: '100%' },
  buttonContainer: {
    marginBottom: '20px',
  },
  aiBanner: {
    marginBottom: '16px',
  },
}));

export const ContactImportEdit: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState('company');
  const { contactImportProcessId } = useParams<{
    contactImportProcessId: string;
  }>();
  const [contactImportProcess, setContactImportProcess] =
    useState<ContactImportProcessDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollCancelRef = useRef<(() => void) | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollCancelRef.current) {
        pollCancelRef.current();
      }
    };
  }, []);

  const loadProcess = useCallback(async () => {
    try {
      const { data } = await apiFetchContactImportProcess(
        contactImportProcessId!
      );
      if (data) {
        setContactImportProcess(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching contact import process:', error);
    }
    return null;
  }, [contactImportProcessId]);

  const startPolling = useCallback(
    async (targetStates: ContactImportState[]) => {
      // Cancel any existing poll
      if (pollCancelRef.current) {
        pollCancelRef.current();
      }
      setIsPolling(true);
      setErrorMessage(null);

      const { promise, cancel } = pollProcessState(
        contactImportProcessId!,
        targetStates
      );
      pollCancelRef.current = cancel;

      try {
        const result = await promise;
        setContactImportProcess(result);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Unknown error'
        );
        await loadProcess();
      } finally {
        setIsPolling(false);
        pollCancelRef.current = null;
      }
    },
    [contactImportProcessId, loadProcess]
  );

  useEffect(() => {
    const init = async () => {
      const data = await loadProcess();
      setIsLoading(false);
      if (!data) return;

      const state = data.state;
      const isPendingOrInProgress =
        isStateEqual(state, ContactImportState.Pending) ||
        isStateEqual(state, ContactImportState.InProgress);

      if (isPendingOrInProgress) {
        // Check if stuck (>5min since updatedAt)
        const updatedAt = new Date(data.updatedAt).getTime();
        const fiveMinAgo = Date.now() - 5 * 60 * 1000;
        if (updatedAt < fiveMinAgo) {
          // Stuck — don't poll, let user retry manually
          setErrorMessage(
            'Der Prozess scheint hängen geblieben zu sein. Bitte "Erneut versuchen" klicken.'
          );
        } else {
          startPolling([ContactImportState.Validated]);
        }
      }
    };
    init();
  }, [loadProcess, startPolling]);

  const handleRetryValidation = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await apiValidateContactImportProcess(contactImportProcessId);
      await startPolling([ContactImportState.Validated]);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Validierung fehlgeschlagen'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartImportService = async () => {
    setIsLoading(true);
    try {
      await apiStartContactImportProcess(contactImportProcessId);
      await startPolling([ContactImportState.Imported]);
    } catch (error) {
      console.error('Error starting contact import process:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessUpdate = useCallback(
    (updatedProcess: ContactImportProcessDto) => {
      setContactImportProcess(updatedProcess);
    },
    []
  );

  const isActionNotSet = (action: any): boolean => {
    const v = typeof action === 'number' ? action : String(action).toLowerCase();
    return v === ContactImportAction.NotSet || v === 'notset' || v === 0;
  };

  const isStateEqual = (state: any, target: ContactImportState): boolean => {
    if (state === target) return true;
    const stateNames: Record<number, string> = {
      [ContactImportState.Pending]: 'pending',
      [ContactImportState.InProgress]: 'inprogress',
      [ContactImportState.Validated]: 'validated',
      [ContactImportState.ImportRequested]: 'importrequested',
      [ContactImportState.Imported]: 'imported',
      [ContactImportState.Failed]: 'failed',
    };
    return String(state).toLowerCase() === stateNames[target];
  };

  const notSetCount = useMemo(() => {
    if (!contactImportProcess) return 0;
    const companyNotSet = (contactImportProcess.externalCompanyImports ?? []).filter(
      (c) => isActionNotSet(c.action)
    ).length;
    const contactNotSet = (contactImportProcess.externalContactImports ?? []).filter(
      (c) => isActionNotSet(c.action)
    ).length;
    const buNotSet = (contactImportProcess.externalBusinessUnitImports ?? []).filter(
      (c) => isActionNotSet(c.action)
    ).length;
    return companyNotSet + contactNotSet + buNotSet;
  }, [contactImportProcess]);

  const isValidated = contactImportProcess
    ? isStateEqual(contactImportProcess.state, ContactImportState.Validated)
    : false;
  const isFailed = contactImportProcess
    ? isStateEqual(contactImportProcess.state, ContactImportState.Failed)
    : false;
  const isPending = contactImportProcess
    ? isStateEqual(contactImportProcess.state, ContactImportState.Pending)
    : false;
  const isInProgress = contactImportProcess
    ? isStateEqual(contactImportProcess.state, ContactImportState.InProgress)
    : false;

  const canStartImport = isValidated && notSetCount === 0 && !isLoading;

  const importDisabledReason = !isValidated
    ? `Status: ${contactImportProcess?.state} (muss "Validated" sein)`
    : notSetCount > 0
    ? `${notSetCount} Einträge haben noch keine Aktion zugewiesen`
    : isLoading
    ? 'Laden...'
    : '';
  // Retry/Validate möglich bei: Pending (mit Daten), Validated, Failed, InProgress (>5min stuck)
  const canRetryValidation =
    !isLoading && !isPolling && (isPending || isValidated || isFailed || isInProgress);

  const renderTabs = () => [
    {
      label: t('settings:contactImporter.tabs.companies'),
      key: 'company',
      content: (
        <ExternalCompanyTab
          contactImportProcess={contactImportProcess}
          onProcessUpdate={handleProcessUpdate}
        />
      ),
    },
    {
      label: t('settings:contactImporter.tabs.contacts'),
      key: 'contacts',
      content: (
        <ExternalContactTab
          contactImportProcess={contactImportProcess}
          onProcessUpdate={handleProcessUpdate}
        />
      ),
    },
  ];

  if (isLoading && !contactImportProcess) {
    return (
      <Flex.Column flex={1} alignItems="center" justifyContent="center">
        <PrioSpinner size="large" />
        <p>{t('settings:contactImportProcess.loading')}</p>
      </Flex.Column>
    );
  }

  if (isPolling) {
    return (
      <Flex.Column flex={1} alignItems="center" justifyContent="center">
        <PrioSpinner size="large" />
        <p>{t('settings:contactImportProcess.validating')}</p>
      </Flex.Column>
    );
  }

  return (
    <div className={classes.root}>
      {(errorMessage || (isFailed && contactImportProcess?.errorMessage)) && (
        <div style={{
          padding: '8px 16px',
          marginBottom: 16,
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: 4,
          color: '#cf1322',
        }}>
          {errorMessage || contactImportProcess?.errorMessage}
        </div>
      )}

      {(contactImportProcess?.aiNormalizedCount ?? 0) > 0 && (
        <div style={{
          padding: '8px 16px',
          marginBottom: 16,
          backgroundColor: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: 4,
        }}>
          {t('settings:contactImportProcess.aiNormalizedBanner', {
            count: contactImportProcess.aiNormalizedCount,
          })}
        </div>
      )}

      {notSetCount > 0 && isValidated && (
        <div style={{
          padding: '8px 16px',
          marginBottom: 16,
          backgroundColor: '#fffbe6',
          border: '1px solid #ffe58f',
          borderRadius: 4,
        }}>
          {t('settings:contactImportProcess.notSetWarning', {
            count: notSetCount,
          })}
        </div>
      )}

      {!canStartImport && importDisabledReason && isValidated === false && (
        <div style={{
          padding: '8px 16px',
          marginBottom: 16,
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: 4,
          color: '#ad6800',
        }}>
          {importDisabledReason}
        </div>
      )}

      <Flex.Row
        alignItems="center"
        childrenGap={10}
        className={classes.buttonContainer}
      >
        <Button
          type="primary"
          disabled={!canStartImport}
          onClick={handleStartImportService}
        >
          {t('settings:contactImportProcess.startImportService')}
          {notSetCount > 0 && isValidated ? ` (${notSetCount})` : ''}
        </Button>
        <Button
          disabled={!canRetryValidation}
          onClick={handleRetryValidation}
        >
          {isFailed
            ? t('settings:contactImportProcess.retry')
            : t('settings:contactImportProcess.renewValidation')}
        </Button>
        <Button
          iconProp={['fal', 'arrows-rotate']}
          disabled={isLoading || isPolling}
          onClick={async () => {
            setIsLoading(true);
            await loadProcess();
            setIsLoading(false);
          }}
        >
          {t('settings:companyImport.reload')}
        </Button>
      </Flex.Row>
      <Tabs
        contentClassName="fullHeightContactImporter"
        defaultTabKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        items={renderTabs()}
      />
    </div>
  );
};
